import { CenturionDialogBanner } from "@/components/centurion/CenturionShell";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Check, Circle, ChevronDown, CalendarCheck, Settings2, BookOpen, Sparkles } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { stageLevels, getStageForGender, type Gender } from "@/lib/avatarData";
import { useGateStatus, formatGateCurrent } from "@/hooks/useGateStatus";
import { useMetricEntries } from "@/hooks/useMetricEntries";
import { useDeskCareer } from "@/hooks/useDeskCareer";
import { useMoneyFoundation, logMoneyOffTrack } from "@/hooks/useMoneyFoundation";
import { PYRAMID, pyramidStatus, nextMoneyAction } from "@/lib/desk/foundations";
import { NumeralCard, DashedBox, roman } from "@/components/centurion/NumeralCard";
import MoneyPyramid from "@/components/money/MoneyPyramid";
import PaintedPyramid from "@/components/money/PaintedPyramid";
import { SetupForm, CheckinForm, Lessons } from "@/components/money/MoneyFoundationCard";

type Action = "setup" | "checkin" | "lessons" | "metrics" | "link";
type Panel = null | "setup" | "checkin" | "lessons";

/** Tools that open with stage. Shown once as a "New" step, never as a permanent list. */
export const STAGE_TOOLS = [
  { name: "Paper Trading", at: 3, to: "/portfolio" },
  { name: "Investment Account", at: 4, to: "/portfolio" },
  { name: "Investing Club", at: 4, to: "/desk" },
];
const seenKey = (n: string) => `decorum-unlock-seen-${n}`;

export interface NextStep { label: string; hint?: string; action: Action; to?: string; tool?: string }
interface Row { key: string; label: string; progress: string; met: boolean; action: Action }

/** Single source of truth for "what to do next", used by the card and the Next Rep banner. */
export function useNextLevel() {
  const { data: gate, isLoading: gLoad } = useGateStatus();
  const { data: entries = [] } = useMetricEntries();
  const { seat } = useDeskCareer();
  const { stats, habits, isLoading: mLoad } = useMoneyFoundation();
  const [seenTick, setSeenTick] = useState(0);

  const stage = gate?.current_stage ?? 1;
  const pyramid = pyramidStatus(stats);
  const moneyReqs = pyramid.reqs;
  const rows: Row[] = [
    ...(gate?.items ?? []).map((i) => ({
      key: `g-${i.id}`, label: i.label, met: i.met, action: "metrics" as Action,
      progress: i.met ? "" : `now ${formatGateCurrent(i)}`,
    })),
    ...moneyReqs.map((r) => ({
      key: `m-${r.key}`, label: r.label, met: r.met, action: r.action as Action,
      progress: r.display ?? `${Math.min(r.current, r.target)}/${r.target}`,
    })),
  ];

  const last = entries.length ? new Date(entries[entries.length - 1].recorded_at).getTime() : 0;
  const stale = last > 0 && Date.now() - last > 14 * 86_400_000;
  const newTool = STAGE_TOOLS.find((t) => stage >= t.at && typeof window !== "undefined" && !localStorage.getItem(seenKey(t.name)));
  const moneyNext = nextMoneyAction(moneyReqs, stats);
  const gateOpen = gate?.items?.find((i) => !i.met);

  let next: NextStep | null;
  if (entries.length === 0) next = { label: "Log your baseline numbers", hint: "Savings, debt, FICO and fitness. This is where your story starts.", action: "metrics" };
  else if (stale) next = { label: "Welcome back, log this week", hint: "It's been over two weeks. One check-in puts you back on track.", action: "metrics" };
  else if (newTool) next = { label: `New: ${newTool.name} unlocked`, hint: "You earned this by leveling up. Take a look.", action: "link", to: newTool.to, tool: newTool.name };
  else if (moneyNext) next = { label: moneyNext.label, hint: moneyNext.hint, action: moneyNext.action as Action };
  else if (gateOpen) next = { label: `Work on: ${gateOpen.label}`, hint: `Now ${formatGateCurrent(gateOpen)}`, action: "metrics" };
  else next = null;

  const markSeen = (tool: string) => { localStorage.setItem(seenKey(tool), "1"); setSeenTick(seenTick + 1); };
  return { gate, stage, rows, next, stats, habits, seat, pyramid, markSeen, isLoading: gLoad || mLoad };
}

const btn = "h-9 px-4 rounded-md text-[11px] uppercase tracking-[0.18em] font-semibold inline-flex items-center gap-1.5";

export default function NextLevelCard({ gender = "male" }: { gender?: Gender }) {
  const { user } = useAuth();
  const { gate, stage, rows, next, stats, habits, pyramid, markSeen, isLoading } = useNextLevel();
  const [panel, setPanel] = useState<Panel>(null);
  const [showDone, setShowDone] = useState(false);

  useEffect(() => { if (user && stats.missedTwo) void logMoneyOffTrack(user.id); }, [user, stats.missedTwo]);

  if (isLoading) return <div className="h-48 animate-pulse rounded-xl border border-border bg-card" />;

  const atTop = stage >= 8;
  const cur = stageLevels[stage - 1];
  const nxt = stageLevels[Math.min(8, gate?.next_stage ?? stage + 1) - 1];
  const curName = cur ? getStageForGender(cur, gender).name : "";
  const nextName = nxt ? getStageForGender(nxt, gender).name : "";
  const open = rows.filter((r) => !r.met);
  const done = rows.filter((r) => r.met);
  const pct = rows.length ? (done.length / rows.length) * 100 : 0;
  const needs = new Set(open.map((r) => r.action));
  const lv = PYRAMID[(pyramid.repair ?? pyramid.working) - 1];

  const go = (a: Action) => (a === "setup" || a === "checkin" || a === "lessons" ? setPanel(a) : null);

  const startBtn = next && (next.action === "link" && next.to ? (
    <Link to={next.to} onClick={() => next.tool && markSeen(next.tool)} className="shrink-0 font-serif text-gold-dark underline underline-offset-2">Open</Link>
  ) : next.action === "metrics" ? (
    <Link to="/metrics" className="shrink-0 font-serif text-gold-dark underline underline-offset-2">Start</Link>
  ) : (
    <button onClick={() => go(next.action)} className="shrink-0 font-serif text-gold-dark underline underline-offset-2">Start</button>
  ));
  const rowGo = (r: Row) => (r.action === "metrics" ? undefined : () => go(r.action));

  return (
    <section className="space-y-4">
      <NumeralCard n={stage} title={atTop ? `Stage ${roman(8)} · ${curName}` : `Stage ${roman(stage)} · ${curName} → ${nextName}`}
        subtitle={`Money streak ${stats.streak} wk${stats.missedTwo ? " · Off track, 2 weeks missed" : ""}`}
        status={atTop ? "Cleared" : open.length === 0 ? "Ready" : "In progress"} progress={pct / 100} progressLabel={`${done.length}/${rows.length} done`}
        tone="active">
        <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-background/60">
          {open.map((r) => {
            const inner = (<><Circle className="h-5 w-5 shrink-0 text-gold/70" /><span className="min-w-0 flex-1 font-serif text-[15px] text-foreground">{r.label}</span><span className="shrink-0 font-serif text-sm text-muted-foreground">{r.progress}</span></>);
            const cls = "flex w-full items-center gap-3 px-3 py-2.5 text-left";
            return r.action === "metrics"
              ? <Link key={r.key} to="/metrics" className={cls}>{inner}</Link>
              : <button key={r.key} onClick={rowGo(r)} className={cls}>{inner}</button>;
          })}
          {done.length > 0 && (
            <button onClick={() => setShowDone(!showDone)} className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm text-muted-foreground">
              <Check className="h-5 w-5 text-gold-dark" /> <span className="flex-1 font-serif">Done ({done.length})</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${showDone ? "rotate-180" : ""}`} />
            </button>
          )}
          {showDone && done.map((r) => <p key={r.key} className="px-3 py-2 pl-11 font-serif text-sm text-muted-foreground line-through">{r.label}</p>)}
        </div>
        {next ? (
          <DashedBox label="Next step">
            <div className="flex items-center gap-3">
              <span className="min-w-0 flex-1"><span className="block font-serif text-[15px]">{next.label}</span>{next.hint && <span className="block text-xs text-muted-foreground">{next.hint}</span>}</span>
              {startBtn}
            </div>
          </DashedBox>
        ) : (
          <DashedBox label={atTop ? "Every stage cleared" : `Ready for Stage ${stage + 1}`}>
            <p className="flex items-center gap-1.5 font-serif"><Sparkles className="h-4 w-4 text-gold-dark" /> Well done, good and faithful servant. Your next stage opens as it's reviewed.</p>
          </DashedBox>
        )}
        {(needs.has("checkin") || needs.has("setup") || needs.has("lessons")) && (
          <div className="flex flex-wrap gap-4 font-serif text-sm text-gold-dark">
            {needs.has("checkin") && <button onClick={() => setPanel("checkin")} className="inline-flex items-center gap-1.5"><CalendarCheck className="h-4 w-4" />{stats.checkedInThisWeek ? "Update check-in" : "Weekly check-in"}</button>}
            {needs.has("setup") && <button onClick={() => setPanel("setup")} className="inline-flex items-center gap-1.5"><Settings2 className="h-4 w-4" />Setup</button>}
            {needs.has("lessons") && <button onClick={() => setPanel("lessons")} className="inline-flex items-center gap-1.5"><BookOpen className="h-4 w-4" />Lessons</button>}
          </div>
        )}
      </NumeralCard>

      <NumeralCard n={2} title={`Foundation · ${pyramid.repair ? `Repair ${PYRAMID[pyramid.repair - 1].name}` : `Building ${lv.name}`}`}
        subtitle={pyramid.level >= 3 ? "Simulation open · keep climbing" : "Stability today · freedom tomorrow"}>
        <PaintedPyramid status={pyramid} className="mx-auto w-full max-w-xl" />
        <p className="text-center font-serif text-foreground/80">Stability opens the Simulation.</p>
      </NumeralCard>

      <Dialog open={panel !== null} onOpenChange={(o) => !o && setPanel(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <CenturionDialogBanner
            title={panel === "setup" ? "Set Your Orders" : panel === "checkin" ? "Weekly Check-in" : "Lessons"}
            tagline={panel === "setup" ? "Order before battle" : panel === "checkin" ? "Small steps. Larger tomorrows." : "Learn. Apply. Rise."}
          />
          {panel === "setup" && <SetupForm habits={habits} onDone={() => setPanel(null)} />}
          {panel === "checkin" && <CheckinForm onDone={() => setPanel(null)} />}
          {panel === "lessons" && <Lessons seatLessons={lv?.lessons ?? []} done={stats.lessonsDone} />}
        </DialogContent>
      </Dialog>
    </section>
  );
}

/** Featured "Next Level · In progress" card linking to the Next Level page. */
export function NextLevelFeature({ gender = "male" }: { gender?: Gender }) {
  const { gate, stage, rows } = useNextLevel();
  const cur = stageLevels[stage - 1];
  const nxt = stageLevels[Math.min(8, gate?.next_stage ?? stage + 1) - 1];
  const done = rows.filter((r) => r.met).length;
  const pct = rows.length ? done / rows.length : 0;
  return (
    <Link to="/next-level" className="flex items-center gap-3 sm:gap-4 rounded-2xl border border-gold/50 bg-card p-4 shadow-sm">
      <span className="grid h-16 w-16 sm:h-20 sm:w-20 shrink-0 place-items-center rounded-full border-2 border-gold font-serif text-3xl text-gold-light shadow-md" style={{ background: "radial-gradient(circle, hsl(var(--bronze)), hsl(var(--bronze-dark)))" }}>{roman(stage)}</span>
      <span className="min-w-0 flex-1">
        <span className="block font-serif text-lg font-semibold text-foreground">Next Level · In progress</span>
        <span className="block truncate font-serif text-sm text-muted-foreground">Stage {roman(stage)} · {cur ? getStageForGender(cur, gender).name : ""} → {nxt ? getStageForGender(nxt, gender).name : ""}</span>
        <span className="mt-2 flex items-center gap-2">
          <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted"><span className="block h-full rounded-full" style={{ width: `${pct * 100}%`, background: "var(--gradient-gold)" }} /></span>
          <span className="shrink-0 text-xs text-muted-foreground">{done}/{rows.length} done</span>
        </span>
      </span>
      <span className="shrink-0 font-serif text-sm text-gold-dark">Open ›</span>
    </Link>
  );
}
